import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { SelectData } from "mui-rff";
import { memo, useEffect, useMemo, useState } from "react";
import { useField } from "react-final-form";
import { FilledTextArea } from "../../../../components/FilledTextArea";
import { TVerificationAppFieldConfigItem } from "../../../app/models";
import {
  formatSelectDisplayValue,
  normalizeSelectData,
} from "../../utils/configFieldDisplay";

type NamedFieldConfig = TVerificationAppFieldConfigItem & { name: string };

type Props = {
  field: NamedFieldConfig;
  label: string;
  value?: unknown;
  disabled?: boolean;
};

type EditableProps = {
  name: string;
  label: string;
  data: SelectData[];
  multiple?: boolean;
  showEmptyOption?: boolean;
};

type SelectWithOtherProps = EditableProps & {
  other: string;
};

function toArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string" && value.length > 0) return [value];
  return [];
}

function toSelectArrayValue(value: unknown): string[] {
  if (typeof value === "string") {
    return value.split(",").filter(Boolean);
  }
  return toArrayValue(value);
}

function uniqStable(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  }
  return result;
}

function normalizeFreeform(value: string): string {
  return value.trim();
}

function getOptionValue(option: SelectData): string {
  return String(option.value);
}

function getOptionLabel(option: SelectData): string {
  return String(option.label);
}

function renderSelectedValue(options: SelectData[], selected: string[]): string {
  return selected
    .map((value) => {
      const option = options.find((item) => getOptionValue(item) === value);
      return option ? getOptionLabel(option) : value;
    })
    .join(", ");
}

function ensureOtherOption(options: SelectData[], other: string): SelectData[] {
  if (options.some((option) => getOptionValue(option) === other)) {
    return options;
  }
  return [...options, { label: other, value: other }];
}

export const SelectField = memo((props: Props) => {
  const { field, label, value, disabled } = props;

  if (disabled) {
    return (
      <FilledTextArea
        value={formatSelectDisplayValue(field, value)}
        name={field.name}
        label={label}
        inputProps={{ disabled: true }}
      />
    );
  }

  const data = normalizeSelectData(field.data);
  const showEmptyOption = !field.required;

  if (field.other) {
    return (
      <SelectWithOther
        name={field.name}
        label={label}
        data={ensureOtherOption(data, field.other)}
        other={field.other}
        multiple={field.multiple}
        showEmptyOption={showEmptyOption}
      />
    );
  }

  return (
    <PlainSelectField
      name={field.name}
      label={label}
      data={data}
      multiple={field.multiple}
      showEmptyOption={showEmptyOption}
    />
  );
});

const PlainSelectField = memo((props: EditableProps) => {
  const {
    name,
    label,
    data,
    multiple = false,
    showEmptyOption = false,
  } = props;
  const { input } = useField<string | string[] | undefined>(name, {
    subscription: { value: true },
  });
  const selectedValue = multiple
    ? toArrayValue(input.value)
    : typeof input.value === "string"
      ? input.value
      : "";
  const selectedValues = Array.isArray(selectedValue) ? selectedValue : [];
  const labelId = `${name}-select-label`;

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const nextValue = event.target.value;
    input.onChange(
      multiple ? toSelectArrayValue(nextValue) : String(nextValue)
    );
    input.onBlur();
  };

  return (
    <FormControl variant="filled" fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        variant="filled"
        multiple={multiple}
        labelId={labelId}
        label={label}
        name={input.name}
        value={selectedValue}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        onChange={handleChange}
        renderValue={
          multiple
            ? (selected) => renderSelectedValue(data, selected as string[])
            : undefined
        }
        inputProps={{
          "data-testid": `${name}-select`,
        }}
      >
        {!multiple && showEmptyOption && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {data.map((option, index) => {
          const optionValue = getOptionValue(option);
          return (
            <MenuItem
              key={`${optionValue}-${index}`}
              value={optionValue}
              disabled={option.disabled}
            >
              {multiple && (
                <Checkbox checked={selectedValues.includes(optionValue)} />
              )}
              {multiple ? (
                <ListItemText primary={getOptionLabel(option)} />
              ) : (
                getOptionLabel(option)
              )}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
});

const SelectWithOther = memo((props: SelectWithOtherProps) => {
  const {
    name,
    label,
    data,
    other,
    multiple = false,
    showEmptyOption = false,
  } = props;
  const { input } = useField<string | string[] | undefined>(name, {
    subscription: { value: true },
  });
  const { options, presetSet } = useMemo(() => {
    const presetValues = data.map(getOptionValue);
    return { options: data, presetSet: new Set(presetValues) };
  }, [data]);
  const currentArray = multiple ? toArrayValue(input.value) : [];
  const currentSingle =
    !multiple && typeof input.value === "string" ? input.value : "";
  const selectedPresets = currentArray.filter((value) =>
    presetSet.has(value)
  );
  const freeformValues = currentArray.filter((value) => !presetSet.has(value));
  const displayPresets =
    freeformValues.length > 0 && !selectedPresets.includes(other)
      ? [...selectedPresets, other]
      : selectedPresets;
  const singleIsPreset = presetSet.has(currentSingle);
  const singleSelectValue =
    currentSingle === "" ? "" : singleIsPreset ? currentSingle : other;
  const showOtherInputs = multiple
    ? selectedPresets.includes(other) || freeformValues.length > 0
    : singleSelectValue === other;
  const labelId = `${name}-select-label`;

  const getDraftsFromValue = () => {
    if (multiple) {
      return freeformValues.length > 0 ? freeformValues : [];
    }
    if (currentSingle && !singleIsPreset && currentSingle !== other) {
      return [currentSingle];
    }
    return [];
  };

  const [otherDrafts, setOtherDrafts] = useState<string[]>(getDraftsFromValue);

  useEffect(() => {
    setOtherDrafts(getDraftsFromValue());
    // The derived values above already include the current form value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value, multiple, presetSet, other]);

  useEffect(() => {
    if (showOtherInputs && otherDrafts.length === 0) {
      setOtherDrafts((drafts) => (drafts.length === 0 ? [""] : drafts));
    }
  }, [showOtherInputs, otherDrafts.length]);

  const commitTouched = () => input.onBlur();

  const commitMultipleValue = (presets: string[], drafts: string[]) => {
    const freeforms = drafts
      .map(normalizeFreeform)
      .filter((value) => value.length > 0);
    const presetsToUse =
      freeforms.length > 0
        ? presets.filter((preset) => preset !== other)
        : presets;

    input.onChange(uniqStable([...presetsToUse, ...freeforms]));
    commitTouched();
  };

  const handleSingleSelect = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;
    if (nextValue === "") {
      input.onChange("");
      setOtherDrafts([]);
      commitTouched();
      return;
    }
    if (nextValue === other) {
      input.onChange(other);
      setOtherDrafts([""]);
      commitTouched();
      return;
    }
    input.onChange(nextValue);
    setOtherDrafts([]);
    commitTouched();
  };

  const handleMultipleSelect = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value;
    const nextPresets = toSelectArrayValue(rawValue);
    const otherWasSelected = displayPresets.includes(other);
    const otherIsNowSelected = nextPresets.includes(other);

    if (otherWasSelected && !otherIsNowSelected) {
      setOtherDrafts([]);
      commitMultipleValue(
        nextPresets.filter((preset) => preset !== other),
        []
      );
      return;
    }

    commitMultipleValue(nextPresets, otherDrafts);

    if (otherIsNowSelected && otherDrafts.length === 0) {
      setOtherDrafts([""]);
    }
  };

  const setOtherAt = (index: number, value: string) => {
    const nextDrafts = otherDrafts.slice();
    nextDrafts[index] = value;
    setOtherDrafts(nextDrafts);

    if (multiple) {
      commitMultipleValue(selectedPresets, nextDrafts);
    } else {
      input.onChange(value || other);
      commitTouched();
    }
  };

  const addOther = () => {
    setOtherDrafts([...otherDrafts, ""]);
  };

  const removeOther = (index: number) => {
    const nextDrafts = otherDrafts.filter((_, itemIndex) => itemIndex !== index);
    setOtherDrafts(nextDrafts);

    if (multiple) {
      commitMultipleValue(selectedPresets, nextDrafts);
    } else if (nextDrafts.length === 0) {
      input.onChange(other);
      commitTouched();
    } else {
      input.onChange(nextDrafts[0] || other);
      commitTouched();
    }
  };

  return (
    <Box>
      <FormControl variant="filled" fullWidth>
        <InputLabel id={labelId}>{label}</InputLabel>
        {!multiple ? (
          <Select
            variant="filled"
            labelId={labelId}
            label={label}
            name={input.name}
            value={singleSelectValue}
            onBlur={input.onBlur}
            onFocus={input.onFocus}
            onChange={handleSingleSelect}
            inputProps={{
              "data-testid": `${name}-select`,
            }}
          >
            {showEmptyOption && (
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
            )}
            {options.map((option, index) => {
              const optionValue = getOptionValue(option);
              return (
                <MenuItem
                  key={`${optionValue}-${index}`}
                  value={optionValue}
                  disabled={option.disabled}
                >
                  {getOptionLabel(option)}
                </MenuItem>
              );
            })}
          </Select>
        ) : (
          <Select
            variant="filled"
            multiple
            labelId={labelId}
            label={label}
            name={input.name}
            value={displayPresets}
            onBlur={input.onBlur}
            onFocus={input.onFocus}
            onChange={handleMultipleSelect}
            renderValue={(selected) =>
              renderSelectedValue(options, selected as string[])
            }
            inputProps={{
              "data-testid": `${name}-select`,
            }}
          >
            {options.map((option, index) => {
              const optionValue = getOptionValue(option);
              return (
                <MenuItem
                  key={`${optionValue}-${index}`}
                  value={optionValue}
                  disabled={option.disabled}
                >
                  <Checkbox checked={displayPresets.includes(optionValue)} />
                  <ListItemText primary={getOptionLabel(option)} />
                </MenuItem>
              );
            })}
          </Select>
        )}
      </FormControl>
      {showOtherInputs && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {otherDrafts.map((draft, index) => (
            <Box
              key={index}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Please specify your selection of 'Other'"
                value={draft}
                onChange={(event) => setOtherAt(index, event.target.value)}
                onBlur={commitTouched}
                inputProps={{
                  "data-testid": `${name}-other-${index}`,
                }}
              />
              {multiple && (
                <IconButton
                  aria-label="Remove other value"
                  data-testid={`${name}-other-remove-${index}`}
                  onClick={() => removeOther(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          {multiple && (
            <Button
              size="small"
              onClick={addOther}
              data-testid={`${name}-other-add`}
              sx={{ alignSelf: "flex-start" }}
            >
              + add more
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
});
